/**
 * Collection Products Section
 * Handles dynamic filtering, pagination, add to cart, and filter modal
 */
class CollectionProducts {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('#filters-form');
    this.gridWrapper = section.querySelector('.collection-products__grid-wrapper');
    this.grid = section.querySelector('.collection-products__grid');
    this.sidebar = section.querySelector('.collection-products__sidebar');
    this.activeFilters = section.querySelector('.collection-products__active-filters');
    this.resetLink = section.querySelector('.collection-products__reset-filters:not(.collection-products__reset-filters--modal)');
    this.sortSelect = section.querySelector('.collection-products__sort-select');
    this.pagination = section.querySelector('.collection-products__pagination');

    // Filter modal elements
    this.filterTrigger = section.querySelector('.collection-products__filter-trigger');
    this.filterModal = section.querySelector('.collection-products__filter-modal');
    this.modalForm = this.filterModal ? this.filterModal.querySelector('#filters-form-modal') : null;

    this.isLoading = false;
    this.abortController = null;

    this.init();
  }

  init() {
    this.initFilters();
    this.initPagination();
    this.initAddToCart();
    this.initFilterAnimations();
    this.initFilterModal();
  }

  // ========================================
  // FILTERS
  // ========================================
  initFilters() {
    if (this.form) {
      this.form.addEventListener('change', this.handleFilterChange.bind(this));
    }

    // Handle active filter tag removal (sidebar)
    if (this.sidebar) {
      this.sidebar.addEventListener('click', (e) => {
        const filterTag = e.target.closest('.collection-products__active-filter');
        if (filterTag) {
          e.preventDefault();
          this.fetchProducts(filterTag.href);
        }
      });
    }

    // Handle reset filters (sidebar)
    if (this.resetLink) {
      this.resetLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.fetchProducts(this.resetLink.href);
      });
    }

    // Handle sort change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        e.preventDefault();
        this.fetchProducts(e.target.value);
      });
    }
  }

  handleFilterChange() {
    this.submitFilterForm(this.form);
  }

  submitFilterForm(form) {
    if (!form) return;

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }

    // Preserve sort parameter
    const currentUrl = new URL(window.location.href);
    const sortBy = currentUrl.searchParams.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    const baseUrl = window.location.pathname;
    const queryString = params.toString();
    const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    this.fetchProducts(newUrl);
  }

  // ========================================
  // FILTER MODAL
  // ========================================
  initFilterModal() {
    if (!this.filterTrigger || !this.filterModal) return;

    const closeBtn = this.filterModal.querySelector('.collection-products__filter-modal-close');
    const overlay = this.filterModal.querySelector('.collection-products__filter-modal-overlay');
    const applyBtn = this.filterModal.querySelector('.collection-products__filter-modal-apply');

    this.filterTrigger.addEventListener('click', () => this.openFilterModal());
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeFilterModal());
    if (overlay) overlay.addEventListener('click', () => this.closeFilterModal());
    if (applyBtn) applyBtn.addEventListener('click', () => this.closeFilterModal());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.filterModal.classList.contains('is-open')) {
        this.closeFilterModal();
      }
    });

    // Modal form changes
    if (this.modalForm) {
      this.modalForm.addEventListener('change', () => this.submitFilterForm(this.modalForm));
    }

    // Modal active filter tag removal
    this.filterModal.addEventListener('click', (e) => {
      const filterTag = e.target.closest('.collection-products__active-filter');
      if (filterTag) {
        e.preventDefault();
        this.fetchProducts(filterTag.href);
      }
      const resetLink = e.target.closest('.collection-products__reset-filters--modal');
      if (resetLink) {
        e.preventDefault();
        this.fetchProducts(resetLink.href);
      }
    });

    // Initial state
    this.updateFilterCount();
    this.updateApplyButtonText();
  }

  openFilterModal() {
    this.filterModal.classList.add('is-open');
    this.filterModal.setAttribute('aria-hidden', 'false');
    this.filterTrigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeFilterModal() {
    this.filterModal.classList.remove('is-open');
    this.filterModal.setAttribute('aria-hidden', 'true');
    this.filterTrigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  updateFilterCount() {
    const countEl = this.section.querySelector('[data-filter-count]');
    if (!countEl) return;

    let count = 0;
    // Count from whichever form is available
    const container = this.filterModal || this.sidebar;
    if (container) {
      count = container.querySelectorAll('.collection-products__filter-checkbox:checked').length;
    }

    if (count > 0) {
      countEl.textContent = count;
      countEl.style.display = '';
    } else {
      countEl.style.display = 'none';
    }
  }

  updateApplyButtonText() {
    const applyBtn = this.filterModal ? this.filterModal.querySelector('.collection-products__filter-modal-apply') : null;
    if (!applyBtn) return;

    const countEl = this.section.querySelector('[data-total-count]');
    if (countEl) {
      const count = countEl.dataset.totalCount;
      const tpl = window.theme?.strings?.viewProductsCount || 'Voir les {{ count }} produits';
      applyBtn.textContent = tpl.replace('{{ count }}', count);
    }
  }

  // ========================================
  // PAGINATION
  // ========================================
  initPagination() {
    if (this.gridWrapper) {
      this.gridWrapper.addEventListener('click', (e) => {
        const paginationLink = e.target.closest('.collection-products__pagination-page, .collection-products__pagination-arrow');
        if (paginationLink && paginationLink.href && !paginationLink.classList.contains('collection-products__pagination-arrow--disabled')) {
          e.preventDefault();
          this.fetchProducts(paginationLink.href);
          this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  // ========================================
  // FETCH PRODUCTS (AJAX)
  // ========================================
  async fetchProducts(url) {
    if (this.isLoading) {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    this.isLoading = true;
    this.abortController = new AbortController();

    this.showLoader();

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid
      const newGrid = doc.querySelector('.collection-products__grid');
      if (newGrid && this.grid) {
        this.grid.innerHTML = newGrid.innerHTML;
        this.initAddToCart();
      }

      // Update pagination
      const newPagination = doc.querySelector('.collection-products__pagination');
      const currentPagination = this.gridWrapper.querySelector('.collection-products__pagination');
      if (newPagination) {
        if (currentPagination) {
          currentPagination.outerHTML = newPagination.outerHTML;
        } else {
          this.gridWrapper.insertAdjacentHTML('beforeend', newPagination.outerHTML);
        }
      } else if (currentPagination) {
        currentPagination.remove();
      }

      // Update filter states in sidebar and modal
      this.updateFilterStatesIn(doc, this.sidebar);
      this.updateFilterStatesIn(doc, this.filterModal ? this.filterModal.querySelector('.collection-products__filter-modal-body') : null);

      // Update active filters in sidebar
      this.updateActiveFilters(doc);

      // Update modal footer (tags + count + apply button)
      this.updateModalFooter(doc);

      // Update filter count badge
      this.updateFilterCount();

      // Update empty state
      const newEmpty = doc.querySelector('.collection-products__empty');
      const currentEmpty = this.gridWrapper.querySelector('.collection-products__empty');
      if (newEmpty && !currentEmpty) {
        this.grid.style.display = 'none';
        this.gridWrapper.insertAdjacentHTML('afterbegin', newEmpty.outerHTML);
      } else if (!newEmpty && currentEmpty) {
        currentEmpty.remove();
        this.grid.style.display = '';
      }

      // Update URL
      window.history.pushState({}, '', url);

      // Animate new products
      this.animateProducts();

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching products:', error);
        window.location.href = url;
      }
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }

  showLoader() {
    if (!this.loader) {
      this.loader = document.createElement('div');
      this.loader.className = 'collection-products__loader';
      this.loader.innerHTML = `
        <div class="collection-products__loader-spinner">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
          </svg>
        </div>
      `;
    }

    this.gridWrapper.classList.add('is-loading');
    if (!this.gridWrapper.contains(this.loader)) {
      this.gridWrapper.appendChild(this.loader);
    }
  }

  hideLoader() {
    this.gridWrapper.classList.remove('is-loading');
    if (this.loader && this.gridWrapper.contains(this.loader)) {
      this.loader.remove();
    }
  }

  animateProducts() {
    const products = this.grid.querySelectorAll('.product-card, .bundle-card');
    products.forEach((product, index) => {
      product.style.opacity = '0';
      product.style.transform = 'translateY(20px)';

      setTimeout(() => {
        product.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        product.style.opacity = '1';
        product.style.transform = 'translateY(0)';
      }, index * 50);
    });
  }

  // ========================================
  // UPDATE FILTER STATES (generic, works on any container)
  // ========================================
  updateFilterStatesIn(doc, container) {
    if (!container) return;

    const newFilters = doc.querySelectorAll('.collection-products__filter-option');
    newFilters.forEach((newOption) => {
      const checkbox = newOption.querySelector('.collection-products__filter-checkbox');
      if (!checkbox) return;

      const name = checkbox.getAttribute('name');
      const value = checkbox.getAttribute('value');

      const currentCheckbox = container.querySelector(
        `.collection-products__filter-checkbox[name="${name}"][value="${value}"]`
      );

      if (currentCheckbox) {
        const currentOption = currentCheckbox.closest('.collection-products__filter-option');

        currentCheckbox.checked = checkbox.checked;

        const currentVisual = currentOption.querySelector('.collection-products__filter-checkbox-visual');
        const newVisual = newOption.querySelector('.collection-products__filter-checkbox-visual');
        if (currentVisual && newVisual) {
          currentVisual.innerHTML = newVisual.innerHTML;
        }

        const currentCount = currentOption.querySelector('.collection-products__filter-count');
        const newCount = newOption.querySelector('.collection-products__filter-count');
        if (currentCount && newCount) {
          currentCount.textContent = newCount.textContent;
        }

        const isDisabled = checkbox.disabled;
        currentCheckbox.disabled = isDisabled;
        currentOption.classList.toggle('collection-products__filter-option--disabled', isDisabled);

        const isActive = newOption.classList.contains('collection-products__filter-option--active');
        currentOption.classList.toggle('collection-products__filter-option--active', isActive);

        const currentValue = currentOption.querySelector('.collection-products__filter-value');
        if (currentValue) {
          currentValue.classList.toggle('collection-products__filter-value--active', isActive);
          currentValue.classList.toggle('collection-products__filter-value--disabled', isDisabled);
        }
      }
    });
  }

  updateActiveFilters(doc) {
    if (!this.sidebar) return;

    let currentActiveFilters = this.sidebar.querySelector('.collection-products__active-filters');
    let currentResetLink = this.sidebar.querySelector('.collection-products__reset-filters:not(.collection-products__reset-filters--modal)');

    const newSidebar = doc.querySelector('.collection-products__sidebar');
    const newActiveFilters = newSidebar ? newSidebar.querySelector('.collection-products__active-filters') : null;
    const newResetLink = newSidebar ? newSidebar.querySelector('.collection-products__reset-filters:not(.collection-products__reset-filters--modal)') : null;

    if (newActiveFilters) {
      if (currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      } else {
        const form = this.sidebar.querySelector('#filters-form');
        if (form) {
          form.insertAdjacentHTML('afterend', newActiveFilters.outerHTML);
        }
      }
    } else if (currentActiveFilters) {
      currentActiveFilters.remove();
    }

    if (newResetLink) {
      if (currentResetLink) {
        currentResetLink.href = newResetLink.href;
      } else {
        const activeFilters = this.sidebar.querySelector('.collection-products__active-filters');
        if (activeFilters) {
          activeFilters.insertAdjacentHTML('afterend', newResetLink.outerHTML);
        }
      }
    } else if (currentResetLink) {
      currentResetLink.remove();
    }

    this.resetLink = this.sidebar.querySelector('.collection-products__reset-filters:not(.collection-products__reset-filters--modal)');
  }

  updateModalFooter(doc) {
    if (!this.filterModal) return;

    const footer = this.filterModal.querySelector('.collection-products__filter-modal-footer');
    if (!footer) return;

    // Update active filter tags
    const modalTags = footer.querySelector('.collection-products__filter-modal-tags');
    const newModalTags = doc.querySelector('.collection-products__filter-modal-tags');
    if (modalTags && newModalTags) {
      modalTags.innerHTML = newModalTags.innerHTML;
    } else if (modalTags) {
      modalTags.innerHTML = '';
    }

    // Update reset link
    const modalReset = footer.querySelector('.collection-products__reset-filters--modal');
    const newModalReset = doc.querySelector('.collection-products__reset-filters--modal');
    if (newModalReset) {
      if (modalReset) {
        modalReset.href = newModalReset.href;
        modalReset.style.display = '';
      } else {
        const tags = footer.querySelector('.collection-products__filter-modal-tags');
        if (tags) {
          tags.insertAdjacentHTML('afterend', newModalReset.outerHTML);
        }
      }
    } else if (modalReset) {
      modalReset.style.display = 'none';
    }

    // Update total count
    const newCountEl = doc.querySelector('[data-total-count]');
    const currentCountEl = this.section.querySelector('[data-total-count]');
    if (newCountEl && currentCountEl) {
      currentCountEl.dataset.totalCount = newCountEl.dataset.totalCount;
    }
    this.updateApplyButtonText();
  }

  // ========================================
  // ADD TO CART
  // ========================================
  initAddToCart() {
    const addToCartButtons = this.section.querySelectorAll('.product-card__cta');

    addToCartButtons.forEach((button) => {
      button.replaceWith(button.cloneNode(true));
    });

    this.section.querySelectorAll('.product-card__cta').forEach((button) => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;

    if (button.classList.contains('is-loading')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    button.classList.add('is-loading');
    const originalText = button.textContent;
    button.innerHTML = `
      <span class="button__spinner">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
        </svg>
      </span>
    `;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Error adding to cart');
      }

      const item = await response.json();

      button.classList.remove('is-loading');
      button.classList.add('is-success');
      button.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="button__check">
          <path d="M5 12l5 5L20 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>${window.theme?.strings?.added || 'Ajouté'}</span>
      `;

      this.updateCartCount();

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      setTimeout(() => {
        button.classList.remove('is-success');
        button.textContent = originalText;
      }, 2000);

    } catch (error) {
      console.error('Add to cart error:', error);

      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.textContent = window.theme?.strings?.error || 'Erreur';

      setTimeout(() => {
        button.classList.remove('is-error');
        button.textContent = originalText;
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const cart = await response.json();

      const cartCounts = document.querySelectorAll('.header__cart-count, [data-cart-count]');
      cartCounts.forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // ========================================
  // FILTER ANIMATIONS
  // ========================================
  initFilterAnimations() {
    const filterGroups = this.section.querySelectorAll('.collection-products__filter-group');

    filterGroups.forEach((details) => {
      details.removeEventListener('toggle', details._toggleHandler);

      details._toggleHandler = () => {
        if (details.open) {
          const options = details.querySelector('.collection-products__filter-options');
          if (options) {
            options.classList.remove('is-animating');
            void options.offsetWidth;
            options.classList.add('is-animating');
          }
        }
      };

      details.addEventListener('toggle', details._toggleHandler);
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="collection-products"]');
  sections.forEach((section) => {
    new CollectionProducts(section);
  });
});
